import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Markdown rendering for an uploaded document's body.
 *
 * Shared by the HTML viewer (which renders the backend's markdown-from-html
 * derivative) and the Markdown viewer (which renders the file's own bytes), so
 * a document reads the same whichever format it arrived in.
 *
 * No rehype-raw: any literal HTML embedded in the document is rendered as text
 * rather than parsed into the DOM, eliminating script/iframe/event-handler
 * injection paths through the markdown source. Heading/list/table styles are
 * wired explicitly because the project doesn't ship the @tailwindcss/typography
 * plugin, so a bare ``prose`` class would render headings at the global reset
 * size (visually indistinguishable from body).
 */
const DocumentMarkdownView = ({ markdown }: { markdown: string }) => {
  return (
    <div className="p-6 max-w-none break-words text-[15px] leading-7">
      <ReactMarkdown
        // Disable single-tilde strikethrough so a lone `~` used to mean
        // "approximately" (e.g. `~($663 million)`) renders literally instead
        // of striking through to the next tilde. Double-tilde
        // `~~strikethrough~~` still works.
        remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
        components={{
          h1: (props) => (
            <h1 className="text-3xl font-bold mt-6 mb-4" {...props} />
          ),
          h2: (props) => (
            <h2 className="text-2xl font-semibold mt-5 mb-3" {...props} />
          ),
          h3: (props) => (
            <h3 className="text-xl font-semibold mt-4 mb-2" {...props} />
          ),
          h4: (props) => (
            <h4 className="text-lg font-semibold mt-3 mb-2" {...props} />
          ),
          p: (props) => <p className="my-3" {...props} />,
          ul: (props) => <ul className="list-disc pl-6 my-3" {...props} />,
          ol: (props) => <ol className="list-decimal pl-6 my-3" {...props} />,
          li: (props) => <li className="my-1" {...props} />,
          a: (props) => (
            <a
              className="text-blue-700 underline"
              target="_blank"
              rel="noreferrer noopener"
              {...props}
            />
          ),
          strong: (props) => <strong className="font-semibold" {...props} />,
          em: (props) => <em className="italic" {...props} />,
          blockquote: (props) => (
            <blockquote
              className="border-l-4 border-gray-300 pl-4 italic my-3"
              {...props}
            />
          ),
          hr: (props) => <hr className="my-4 border-gray-200" {...props} />,
          code: (props) => (
            <code
              className="bg-gray-100 px-1 py-0.5 rounded text-sm"
              {...props}
            />
          ),
          pre: (props) => (
            <pre
              className="bg-gray-100 p-3 rounded text-sm overflow-x-auto my-3"
              {...props}
            />
          ),
          table: (props) => (
            <div className="overflow-x-auto my-3">
              <table className="border-collapse" {...props} />
            </div>
          ),
          thead: (props) => <thead className="bg-gray-50" {...props} />,
          th: (props) => (
            <th
              className="border border-gray-300 px-3 py-1 text-left font-semibold"
              {...props}
            />
          ),
          td: (props) => (
            <td className="border border-gray-300 px-3 py-1" {...props} />
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

export default DocumentMarkdownView;
